import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Plus, Check, Star, Trash2, Calendar, Clock, Filter, Loader2, ExternalLink
} from 'lucide-react';
import { Task, TaskType } from '../types/task';
import { Lead } from '../types/lead';
import { createTask, toggleTaskComplete, deleteTask } from '../lib/tasksManagement';
import PageHeader from '../components/Layout/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';

interface TasksPageProps {
  tasks: Task[];
  leads: Lead[];
  currentUserId?: string;
  onTasksChange: (tasks: Task[]) => void;
}

type FilterType = 'all' | 'task' | 'appointment' | 'important';

const TasksPage: React.FC<TasksPageProps> = ({ tasks, leads, currentUserId, onTasksChange }) => {
  const [filter, setFilter] = useState<FilterType>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newType, setNewType] = useState<TaskType>('task');
  const [newDueDate, setNewDueDate] = useState('');
  const [newLeadId, setNewLeadId] = useState('');
  const [newImportant, setNewImportant] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return tasks.filter(t => {
      if (filter === 'all') return true;
      if (filter === 'important') return t.is_important;
      return t.type === filter;
    });
  }, [tasks, filter]);

  const pending = filtered.filter(t => !t.completed);
  const completed = filtered.filter(t => t.completed);

  const handleAdd = async () => {
    if (!newTitle.trim() || saving) return;
    setSaving(true);
    const task = await createTask({
      title: newTitle.trim(),
      description: newDescription.trim() || undefined,
      type: newType,
      due_date: newDueDate || null,
      lead_id: newLeadId || null,
      is_important: newImportant,
      completed: false,
      created_by: currentUserId,
    });
    if (task) {
      // Attach lead name
      const leadName = leads.find(l => l.id === newLeadId)?.name;
      onTasksChange([...tasks, { ...task, lead_name: leadName }]);
      setNewTitle('');
      setNewDescription('');
      setNewType('task');
      setNewDueDate('');
      setNewLeadId('');
      setNewImportant(false);
      setShowAddForm(false);
    }
    setSaving(false);
  };

  const handleToggle = async (task: Task) => {
    await toggleTaskComplete(task.id, !task.completed);
    onTasksChange(tasks.map(t => t.id === task.id ? { ...t, completed: !t.completed } : t));
  };

  const handleDelete = async (id: string) => {
    await deleteTask(id);
    onTasksChange(tasks.filter(t => t.id !== id));
    setConfirmDeleteId(null);
  };

  const generateGoogleCalendarLink = (task: Task) => {
    if (!task.due_date) return null;
    const start = new Date(task.due_date);
    const end = new Date(start.getTime() + 60 * 60 * 1000); // +1h
    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const text = encodeURIComponent(task.title);
    const desc = encodeURIComponent(task.description || task.lead_name || '');
    return `https://www.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${fmt(start)}/${fmt(end)}&details=${desc}`;
  };

  const formatDate = (iso: string) => new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'
  });

  const isPast = (iso: string) => new Date(iso) < new Date();

  const FILTERS: { id: FilterType; label: string }[] = [
    { id: 'all', label: 'Alle' },
    { id: 'task', label: 'Aufgaben' },
    { id: 'appointment', label: 'Termine' },
    { id: 'important', label: 'Wichtig' },
  ];

  return (
    <div>
      <PageHeader
        title="Tasks"
        subtitle={`${pending.length} ausstehend`}
        actions={
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Neue Aufgabe</span>
          </button>
        }
      />

      <div className="px-4 md:px-8 py-4 space-y-4">
        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                filter === f.id ? 'bg-zinc-900 text-white' : 'bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Add Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewType('task')}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${newType === 'task' ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}
                  >
                    Aufgabe
                  </button>
                  <button
                    onClick={() => setNewType('appointment')}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${newType === 'appointment' ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}
                  >
                    Termin
                  </button>
                </div>
                <input
                  type="text"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  placeholder={newType === 'task' ? 'Aufgabe beschreiben...' : 'Terminbezeichnung...'}
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  autoFocus
                />
                <input
                  type="text"
                  value={newDescription}
                  onChange={e => setNewDescription(e.target.value)}
                  placeholder="Beschreibung (optional)"
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none"
                />
                <div className="flex gap-2">
                  <input
                    type="datetime-local"
                    value={newDueDate}
                    onChange={e => setNewDueDate(e.target.value)}
                    className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none"
                  />
                  <select
                    value={newLeadId}
                    onChange={e => setNewLeadId(e.target.value)}
                    className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none"
                  >
                    <option value="">Keine Anfrage</option>
                    {leads.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newImportant}
                      onChange={e => setNewImportant(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm text-zinc-600 font-medium flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-amber-400" />
                      Wichtig
                    </span>
                  </label>
                  <div className="flex gap-2">
                    <button onClick={() => setShowAddForm(false)} className="px-3 py-2 border border-zinc-200 rounded-lg text-sm hover:bg-zinc-50">Abbrechen</button>
                    <button
                      onClick={handleAdd}
                      disabled={!newTitle.trim() || saving}
                      className="flex items-center gap-1.5 px-4 py-2 bg-zinc-900 text-white text-sm font-semibold rounded-lg disabled:opacity-40 hover:bg-zinc-800"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {saving ? 'Speichern...' : 'Erstellen'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Task Lists */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-zinc-400">
            <p className="text-sm">Keine {filter === 'all' ? 'Aufgaben' : filter === 'appointment' ? 'Termine' : 'Aufgaben'}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pending */}
            {pending.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-3">Ausstehend</h3>
                <div className="space-y-2">
                  <AnimatePresence initial={false}>
                    {pending.map(task => (
                      <TaskItem
                        key={task.id}
                        task={task}
                        onToggle={handleToggle}
                        onDelete={setConfirmDeleteId}
                        formatDate={formatDate}
                        isPast={isPast}
                        generateGoogleCalendarLink={generateGoogleCalendarLink}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* Completed */}
            {completed.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-widest mb-3">Erledigt ({completed.length})</h3>
                <div className="space-y-2 opacity-60">
                  {completed.slice(0, 5).map(task => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      onToggle={handleToggle}
                      onDelete={setConfirmDeleteId}
                      formatDate={formatDate}
                      isPast={isPast}
                      generateGoogleCalendarLink={generateGoogleCalendarLink}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Aufgabe löschen?"
        message="Diese Aufgabe wird unwiderruflich gelöscht."
        onConfirm={() => confirmDeleteId && handleDelete(confirmDeleteId)}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
};

const TaskItem: React.FC<{
  task: Task;
  onToggle: (task: Task) => void;
  onDelete: (id: string) => void;
  formatDate: (iso: string) => string;
  isPast: (iso: string) => boolean;
  generateGoogleCalendarLink: (task: Task) => string | null;
}> = ({ task, onToggle, onDelete, formatDate, isPast, generateGoogleCalendarLink }) => {
  const calLink = task.type === 'appointment' ? generateGoogleCalendarLink(task) : null;
  const overdue = task.due_date && !task.completed && isPast(task.due_date);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={`flex items-start gap-3 p-3.5 border rounded-xl group transition-all hover:shadow-sm ${
        task.completed ? 'bg-zinc-50 border-zinc-100' : 'bg-white border-zinc-200'
      }`}
    >
      <button
        onClick={() => onToggle(task)}
        className={`w-5 h-5 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
          task.completed ? 'bg-zinc-900 border-zinc-900' : 'border-zinc-300 hover:border-zinc-900'
        }`}
      >
        {task.completed && <Check className="w-3 h-3 text-white" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
            task.type === 'appointment' ? 'bg-violet-100 text-violet-700' : 'bg-zinc-100 text-zinc-600'
          }`}>
            {task.type === 'appointment' ? 'Termin' : 'Aufgabe'}
          </span>
          {task.is_important && <Star className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" fill="currentColor" />}
        </div>
        <p className={`text-sm font-semibold mt-1 ${task.completed ? 'line-through text-zinc-400' : 'text-zinc-900'}`}>
          {task.title}
        </p>
        {task.description && (
          <p className="text-xs text-zinc-500 mt-0.5">{task.description}</p>
        )}
        {task.lead_name && (
          <p className="text-xs text-zinc-400 mt-0.5">📌 {task.lead_name}</p>
        )}
        {task.due_date && (
          <div className="flex items-center gap-1 mt-1.5">
            {task.type === 'appointment' ? <Calendar className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
            <span className={`text-xs font-medium ${overdue ? 'text-red-500' : 'text-zinc-400'}`}>
              {formatDate(task.due_date)}
              {overdue && ' · Überfällig'}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        {calLink && (
          <a
            href={calLink}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 text-zinc-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
            title="In Google Kalender öffnen"
            onClick={e => e.stopPropagation()}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        <button
          onClick={() => onDelete(task.id)}
          className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
};

export default TasksPage;
